/**
 * Augmented Live interactive markup - shared bridge (ENG-6766 / ENG-6788).
 *
 * The selection-to-comment feature needs the same bridge script and message
 * shape in two places: the authenticated console preview
 * (`live-markup-overlay.tsx`, srcDoc iframe) and the public live shell
 * (`renderShell()` in publisher.ts, which fetches its content and renders it
 * via srcDoc). Keeping the script + the message contract here means both
 * surfaces inject byte-identical bridge code and agree on the postMessage
 * envelope - there is a single source of truth.
 *
 * The artifact body runs in a `sandbox="allow-scripts"` iframe with NO
 * `allow-same-origin`, so the parent cannot read the iframe's selection
 * directly (that would throw a SecurityError, and granting same-origin would
 * let untrusted artefact HTML reach our origin). Instead this bridge is
 * injected into the artefact and postMessages the selection (text + rect) up
 * to the parent, which anchors a small comment prompt over the iframe.
 *
 * This module is pure (strings + plain functions, no DOM, no node:*) so it is
 * safe to import from core, the webapp, and Workers alike.
 *
 * Scope: text selection -> comment (ENG-6766), inline click-to-edit (ENG-6821),
 * and right-click -> comment on any non-text element (image, background,
 * container) via the contextmenu handler (ENG-6847).
 */

/** Envelope marker stamped on every bridge message so the parent can filter. */
export const MARKUP_MARKER = '__augmentedLiveMarkup';

export interface MarkupSelectionRect {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
}

export type MarkupBridgeMessage =
  | {
      [MARKUP_MARKER]: true;
      type: 'selection';
      text: string;
      rect: MarkupSelectionRect;
      /**
       * CSS selector for the nearest ancestor of the selection that carries a
       * stable anchor (`[data-al-id="…"]`, else `#id`). Empty string when the
       * selection has no anchored ancestor. Lets the agent target the element
       * structurally instead of by fragile text-match (ENG-6802).
       */
      target: string;
    }
  | { [MARKUP_MARKER]: true; type: 'clear' }
  // ENG-6821: a viewer saved an inline text edit. `path` is element-child indices
  // from <body> to the edited element (the server walks source_html the same way);
  // oldText is the original (concurrency guard), newText the replacement.
  //
  // ENG-6856: when `textIndex` is present, `path` points at an INLINE-ONLY
  // container (its element children are only inline-formatting tags) and the edit
  // targets the textIndex-th TEXT NODE among that element's child nodes - so the
  // text is replaced in place and the nested inline markup (spans, strong/em, br)
  // around it is byte-preserved. Absent ⇒ the legacy whole-leaf-element edit.
  | {
      [MARKUP_MARKER]: true;
      type: 'edit';
      path: number[];
      oldText: string;
      newText: string;
      textIndex?: number;
    }
  // ENG-6847: a viewer right-clicked a non-text element to comment on it. `label`
  // is a human-readable reference ('image (hero.png)', 'background image',
  // '<section> Pricing') the parent quotes in the prompt; `target` is the same
  // structural-anchor selector convention as a text selection (ENG-6802); `rect`
  // anchors the prompt over the element.
  | {
      [MARKUP_MARKER]: true;
      type: 'element';
      label: string;
      target: string;
      rect: MarkupSelectionRect;
    };

/**
 * Parent -> content control messages (ENG-6821 / ENG-6847). The shell tells the
 * content iframe whether the viewer may edit text inline and/or comment on
 * elements (only after the auth probe confirms it), and relays the save result
 * back so the content can clear/revert the editing UI.
 *
 * `enable-edit` and `enable-comment` are separate gates on purpose: the public
 * shell arms both for an authed member, but the console preview arms ONLY
 * `enable-comment` (inline edit stays a public-shell affordance, ENG-6821). The
 * right-click element-comment handler suppresses the browser's native context
 * menu, so it must stay disarmed for anon viewers - hence a flag, not always-on.
 */
export const MARKUP_CONTROL_MARKER = '__augmentedLiveMarkupControl';
export type MarkupControlMessage =
  | { [MARKUP_CONTROL_MARKER]: true; type: 'enable-edit' }
  | { [MARKUP_CONTROL_MARKER]: true; type: 'enable-comment' }
  // ENG-8477: `enable-edit` says the viewer MAY edit; `set-edit-mode` says they
  // have asked to right now. The split exists because an artefact can cover its
  // own content (a deck's click zones), and there the same press cannot both
  // page the deck and open an editor - so piercing those overlays has to be
  // something the viewer opts into, not a default that breaks navigation.
  | { [MARKUP_CONTROL_MARKER]: true; type: 'set-edit-mode'; on: boolean }
  | { [MARKUP_CONTROL_MARKER]: true; type: 'edit-result'; ok: boolean; error?: string };

/**
 * The bridge script injected into the artefact iframe. Posts the current text
 * selection (or a clear) up to the parent on mouseup; clears on scroll so a
 * stale prompt doesn't float over moved content. Uses '*' targetOrigin because
 * the parent's origin isn't known to the sandboxed (opaque-origin) frame - the
 * payload is only the user's own selected text, never a secret. The parent
 * authenticates the message by checking event.source against the iframe window.
 */
export const MARKUP_BRIDGE_SCRIPT = `<script>(function(){
  function post(m){ try { parent.postMessage(Object.assign({${MARKUP_MARKER}:true}, m), '*'); } catch(e){} }
  // Nearest ancestor carrying a stable anchor, as a CSS selector. Prefer
  // data-al-id (the durable convention) over an incidental id. '' if none.
  // Values are run through CSS.escape so a value with quotes/colons/brackets
  // can't produce a broken or injected selector.
  function esc(v){ try { return (self.CSS && self.CSS.escape) ? self.CSS.escape(v) : v; } catch(e){ return v; } }
  // Nearest anchored ancestor of an element, as a CSS selector ('' if none).
  function anchorOf(el){
    try {
      var hit = (el && el.closest) ? el.closest('[data-al-id],[id]') : null;
      if(!hit) return '';
      var dal = hit.getAttribute('data-al-id');
      if(dal) return '[data-al-id="' + esc(dal) + '"]';
      return hit.id ? ('#' + esc(hit.id)) : '';
    } catch(e){ return ''; }
  }
  function anchorFor(range){
    try {
      var node = range.commonAncestorContainer;
      var el = (node && node.nodeType === 1) ? node : (node ? node.parentElement : null);
      return anchorOf(el);
    } catch(e){ return ''; }
  }
  // Human-readable reference to a right-clicked element, kept to one line so it
  // reads cleanly as the quoted context in the comment prompt. Names images by
  // alt/filename, background-image elements by filename, otherwise tag + a short
  // text snippet so the agent knows exactly what was clicked (ENG-6847).
  function basename(u){ try { return (u||'').split('?')[0].split('#')[0].split('/').pop() || (u||''); } catch(e){ return u||''; } }
  function describeEl(el){
    try {
      var tag=(el.tagName||'').toLowerCase();
      if(tag==='img'){
        var alt=(el.getAttribute('alt')||'').trim();
        if(alt) return 'image: ' + alt;
        var src=el.getAttribute('src')||el.currentSrc||'';
        var n=basename(src);
        return n ? ('image (' + n + ')') : 'image';
      }
      var bg='';
      try { bg=(self.getComputedStyle ? self.getComputedStyle(el).backgroundImage : '') || ''; } catch(e){}
      if(bg && bg.indexOf('url(')!==-1){
        var m=bg.match(/url\\(\\s*["']?([^"')]+)["']?\\s*\\)/);
        var bn=(m && m[1]) ? basename(m[1]) : '';
        return bn ? ('background image (' + bn + ')') : 'background image';
      }
      var txt=(el.textContent||'').replace(/\\s+/g,' ').trim();
      if(tag==='svg' || (!txt && el.querySelector && el.querySelector('svg'))) return 'icon (<' + (tag||'svg') + '>)';
      if(txt) return '<' + (tag||'element') + '> ' + (txt.length>60 ? txt.slice(0,57)+'…' : txt);
      return '<' + (tag||'element') + '> element';
    } catch(e){ return 'element'; }
  }

  // ===== ENG-6821: inline click-to-edit =====
  var editEnabled = false;    // armed only after the shell confirms the viewer may edit
  var commentEnabled = false; // armed when the viewer may comment (ENG-6847 right-click)
  var editing = null;         // the element currently in edit mode
  var bar = null;             // the Save/Cancel toolbar
  // ENG-8477: the viewer has explicitly turned editing ON from the shell. Only
  // then does the bridge look underneath the artefact's own overlays. See the
  // long note on the window-capture click handler for why this is a mode and
  // not just always-on.
  var editMode = false;
  // ENG-9383: the ONE gate every edit-entry path asks. It exists because the
  // last time a path was added, the old ones did not come with it: ENG-8477
  // introduced editMode for the deep-probe handlers and left the ENG-6821
  // direct click/hover on an arming-only check, so text stayed editable with
  // the toggle off and the button gated one of two paths. A funnel is the fix
  // for that class of bug; adding the term twice is only the fix for this
  // instance of it. A fifth handler cannot forget the mode.
  function canEdit(){ return editEnabled && editMode && !editing; }
  var hoverCued = []; // ENG-9383: elements currently wearing the mouseover cue

  // Element-child indices from <body> down to el. Counts ELEMENT children only
  // (children, not childNodes), matching how the server walks source_html; our
  // injected style/script sit at the end of <body>, so the authored content's
  // elements keep their indices.
  function pathTo(el){
    var path=[], n=el;
    while(n && n!==document.body && n.parentElement){
      path.unshift(Array.prototype.indexOf.call(n.parentElement.children, n));
      n=n.parentElement;
    }
    return path;
  }
  // Only LEAF text elements are editable. An element with element children is a
  // container - editing it would send the container's path + a flattened
  // textContent, replacing the whole subtree and destroying nested markup. Saving
  // is text-of-one-leaf only, preserving byte-fidelity everywhere else.
  function editable(el){
    if(!el || el.nodeType!==1) return false;
    if(el===document.body || el===document.documentElement) return false;
    if(el.children && el.children.length>0) return false;
    var tag=el.tagName;
    if(tag==='SCRIPT'||tag==='STYLE'||tag==='A'||tag==='BUTTON'||tag==='INPUT'||tag==='TEXTAREA'||tag==='IMG') return false;
    if(el.closest && el.closest('[data-al-noedit]')) return false;
    return ((el.textContent||'').trim().length>0);
  }
  function removeBar(){ if(bar){ try{bar.remove();}catch(e){} bar=null; } }
  // ENG-6856: inline-formatting tags whose presence inside a container still lets
  // us edit that container's bare text nodes per-node - the nested markup survives
  // because we replace only the targeted TEXT node, never the whole subtree.
  var INLINE_OK={SPAN:1,STRONG:1,EM:1,B:1,I:1,U:1,SMALL:1,MARK:1,SUB:1,SUP:1,CODE:1,BR:1,ABBR:1,WBR:1,Q:1,CITE:1,TIME:1};
  // A container is per-text-node editable when EVERY element child is an inline
  // formatting tag (so it is not a leaf, but has no block / link / control child
  // an in-place text edit could strand). <a> is deliberately absent from INLINE_OK:
  // a link child blocks editing so an edit can never clobber an href.
  function inlineOnlyEditable(el){
    if(!el || el.nodeType!==1) return false;
    if(el===document.body || el===document.documentElement) return false;
    var tag=el.tagName;
    if(tag==='SCRIPT'||tag==='STYLE'||tag==='A'||tag==='BUTTON'||tag==='INPUT'||tag==='TEXTAREA'||tag==='IMG') return false;
    if(el.closest && el.closest('[data-al-noedit]')) return false;
    var kids=el.children; if(!kids || kids.length===0) return false; // leaves use editable()
    for(var i=0;i<kids.length;i++){ if(!INLINE_OK[kids[i].tagName]) return false; }
    return ((el.textContent||'').trim().length>0);
  }
  // The text node under the pointer, so a click on bare text inside an inline-only
  // container targets exactly that run (not its inline siblings).
  function textNodeAtPoint(x,y){
    try{
      if(document.caretRangeFromPoint){ var r=document.caretRangeFromPoint(x,y); return (r && r.startContainer && r.startContainer.nodeType===3) ? r.startContainer : null; }
      if(document.caretPositionFromPoint){ var p=document.caretPositionFromPoint(x,y); return (p && p.offsetNode && p.offsetNode.nodeType===3) ? p.offsetNode : null; }
      return null;
    }catch(e){ return null; }
  }
  // ENG-8477: the topmost element under the pointer that we could actually edit,
  // looking THROUGH anything the artefact has laid over its own content.
  //
  // elementsFromPoint returns the full hit stack, front to back. We walk it from
  // the front and take the first genuinely editable element, skipping our own
  // injected UI. An artefact overlay is skipped for free: it is an empty div, so
  // editable() rejects it on the text check and we keep walking.
  //
  // Returns null when there is nothing editable under the pointer at all, which
  // is the signal to leave the click completely alone.
  function editableAtPoint(x,y){
    var stack;
    try{ stack=document.elementsFromPoint(x,y)||[]; }catch(e){ return null; }
    for(var i=0;i<stack.length;i++){
      var el=stack[i];
      if(!el || el.nodeType!==1) continue;
      if(el.closest && el.closest('[data-al-noedit]')) continue; // our toolbar, not content
      if(editable(el)||inlineOnlyEditable(el)) return el;
    }
    return null;
  }
  // The caret probe (caretRangeFromPoint) reports on the TOPMOST element at the
  // point, so an overlay swallows it exactly as it swallows the click - it hands
  // back the overlay div, not the text underneath. Neutralise everything stacked
  // above the target for the length of the probe, then put it back. The restore
  // is in a finally and runs before the browser can paint, so nothing flickers
  // and no artefact state is left modified.
  // This re-queries elementsFromPoint rather than reusing the caller's stack, so
  // target is NOT guaranteed to still be in it. Find target's index first and
  // mute only the prefix in front of it: a not-found target must fall back to the
  // plain probe, because muting the whole stack would neutralise target and its
  // ancestors too - a wider style mutation than intended, for a probe whose
  // result the caller then rejects anyway.
  function textNodeAtPointThrough(x,y,target){
    return throughOverlays(x,y,target,function(){ return textNodeAtPoint(x,y); });
  }
  // Run probe() with everything stacked IN FRONT of target muted for pointer
  // hit-testing, then restore it. Shared by the text-node probe above and the
  // caret placement below (ENG-10632), so there is one overlay-muting path.
  // A target that is not in the hit stack (or no elementsFromPoint at all) runs
  // the probe unmuted, for the reason given on textNodeAtPointThrough.
  function throughOverlays(x,y,target,probe){
    var stack;
    try{ stack=document.elementsFromPoint(x,y)||[]; }catch(e){ return probe(); }
    var cut=-1, i;
    for(i=0;i<stack.length;i++){ if(stack[i]===target || (target && target.contains && stack[i] && stack[i].nodeType && target.contains(stack[i]))){ cut=i; break; } }
    if(cut<0) return probe();
    var muted=[];
    for(i=0;i<cut;i++){
      muted.push([stack[i], stack[i].style ? stack[i].style.pointerEvents : '']);
      try{ stack[i].style.pointerEvents='none'; }catch(e){}
    }
    try{ return probe(); }
    finally{
      for(i=0;i<muted.length;i++){ try{ muted[i][0].style.pointerEvents=muted[i][1]||''; }catch(e){} }
    }
  }
  // ENG-10632: is this pointer event aimed INSIDE the open editor?
  //
  // 'direct' - the event's own target is the editor or inside it (no overlay).
  // 'through' - the target is something laid over the editor (a deck click
  //   zone), but the editor is in the hit stack under the pointer.
  // null     - no edit is open, or the press is genuinely elsewhere.
  //
  // WHY NOT e.target ALONE. That is exactly the ENG-8477 blind spot: a pointer
  // event targets the HIT-TEST result, and a deck's full-viewport zones win the
  // hit test, so e.target is the zone for every press on slide text. ENG-8477
  // fixed the click that OPENS an editor this way and left the guards that
  // protect an OPEN editor keyed on e.target, so the second click - the one that
  // places the caret - was handed to the zone and paged the slide.
  //
  // A keydown is different: it targets the FOCUSED element, so the ENG-8500 key
  // guard's e.target test is correct and stays as it is.
  function pointInEditor(e){
    if(!editing || !e) return null;
    var t=e.target;
    if(t===editing || (editing.contains && t && t.nodeType && editing.contains(t))) return 'direct';
    if(typeof e.clientX!=='number' || typeof e.clientY!=='number') return null;
    var stack;
    try{ stack=document.elementsFromPoint(e.clientX, e.clientY)||[]; }catch(err){ return null; }
    for(var i=0;i<stack.length;i++){
      var n=stack[i];
      if(n===editing || (editing.contains && n && n.nodeType && editing.contains(n))) return 'through';
    }
    return null;
  }
  // ENG-10632: put the caret where the viewer pressed, as the browser would have
  // if nothing covered the editor. The browser's own caret placement goes to the
  // topmost element, which is the overlay, so it places nothing in the text.
  // Returns true when a caret was placed inside the editor.
  function placeCaretAtPointThrough(x,y,el){
    return throughOverlays(x,y,el,function(){
      try{
        var node=null, offset=0, r=null;
        if(document.caretRangeFromPoint){
          r=document.caretRangeFromPoint(x,y);
          if(r){ node=r.startContainer; offset=r.startOffset||0; }
        } else if(document.caretPositionFromPoint){
          var p=document.caretPositionFromPoint(x,y);
          if(p){ node=p.offsetNode; offset=p.offset||0; }
        }
        if(!node || !(node===el || (el.contains && el.contains(node.nodeType===3 ? node.parentNode||node.parentElement : node)))) return false;
        if(!r){ r=document.createRange(); r.setStart(node, offset); r.collapse(true); }
        var s=document.getSelection(); s.removeAllRanges(); s.addRange(r);
        return true;
      }catch(err){ return false; }
    });
  }
  // Index of a text node among its parent's child TEXT nodes, in order - the same
  // counting the server uses to re-locate it in source_html (ENG-6856).
  function textIndexOf(container, tn){
    var i=-1, ns=container.childNodes;
    for(var k=0;k<ns.length;k++){ if(ns[k].nodeType===3){ i++; if(ns[k]===tn) return i; } }
    return -1;
  }
  function keepCaretIn(el){
    try{
      var s=document.getSelection(), n=s && s.rangeCount ? s.anchorNode : null;
      if(n && (n===el || (el.contains && el.contains(n.nodeType===3 ? n.parentNode : n)))) return;
    }catch(err){}
    placeCaretEnd(el);
  }
  function placeCaretEnd(el){ try{ var r=document.createRange(); r.selectNodeContents(el); r.collapse(false); var s=document.getSelection(); s.removeAllRanges(); s.addRange(r); }catch(e){} }
  // The Save/Cancel toolbar anchored above el. Shared by whole-element edits
  // (startEdit) and per-text-node edits (startEditTextNode).
  function showBar(el){
    bar=document.createElement('div');
    bar.setAttribute('data-al-noedit','1');
    bar.style.cssText='position:fixed;z-index:2147483647;display:flex;gap:6px;font:600 13px system-ui,-apple-system,sans-serif';
    var r=el.getBoundingClientRect();
    bar.style.top=Math.max(8,r.top-40)+'px'; bar.style.left=Math.max(8,r.left)+'px';
    function mk(label,bg,fn){ var b=document.createElement('button'); b.type='button'; b.textContent=label;
      b.style.cssText='border:0;border-radius:6px;padding:6px 12px;cursor:pointer;color:#fff;box-shadow:0 2px 8px rgba(0,0,0,.3);background:'+bg;
      b.addEventListener('mousedown', function(e){ e.preventDefault(); }); // keep focus/text
      b.addEventListener('click', function(e){ e.preventDefault(); fn(); }); return b; }
    bar.appendChild(mk('Save','#0b7a4b',function(){ endEdit(true); }));
    bar.appendChild(mk('Cancel','#475569',function(){ endEdit(false); }));
    document.body.appendChild(bar);
  }
  function endEdit(save){
    if(!editing) return;
    var el=editing; editing=null;
    removeBar();
    // Compare on normalized text (matches the server's whitespace-tolerant guard),
    // but send the RAW edit so intentional spacing survives and clearing a block
    // (newText '') is a real delete. The server re-scans before publishing.
    var rawNewText=el.textContent||'';
    var normNewText=rawNewText.replace(/\\s+/g,' ').trim();
    if(el.__alTextIndex!==undefined){
      // ENG-6856 per-text-node edit: el is our temporary editable wrapper span.
      // Unwrap back to a plain text node either way (a successful save's republish
      // hot-swap reloads the canonical version); on save also post the text-node
      // edit, carrying textIndex so the server replaces only that run in place.
      try{ el.parentNode.replaceChild(document.createTextNode(save?rawNewText:el.__alOldRaw), el); }catch(e){}
      if(save && normNewText!==el.__alOld){
        post({type:'edit', path:el.__alPath, textIndex:el.__alTextIndex, oldText:el.__alOld, newText:rawNewText});
      }
      return;
    }
    // Legacy whole-leaf-element edit.
    try{ el.removeAttribute('contenteditable'); el.style.outline=el.__alOut||''; }catch(e){}
    if(save && normNewText!==el.__alOld){
      post({type:'edit', path:el.__alPath, oldText:el.__alOld, newText:rawNewText});
    } else if(!save){
      try{ el.textContent=el.__alOldRaw; }catch(e){}
    }
  }
  function startEdit(el){
    if(editing) endEdit(false);
    editing=el;
    el.__alOldRaw=el.textContent;
    el.__alOld=(el.textContent||'').replace(/\\s+/g,' ').trim();
    el.__alPath=pathTo(el);
    el.__alOut=el.style.outline;
    el.style.outline='2px solid #6ee7b7';
    el.setAttribute('contenteditable','true');
    try{ el.focus(); }catch(e){}
    showBar(el);
  }
  // ENG-6856: edit one bare text node inside an inline-only container. We swap the
  // text node for a contenteditable wrapper span (so only this run is editable and
  // the inline siblings stay put) and record the container path + the text node's
  // index for the server to replace it in place. Index is computed BEFORE the swap
  // so it matches source_html's untouched text-node order.
  function startEditTextNode(tn, container){
    if(editing) endEdit(false);
    var idx=textIndexOf(container, tn);
    if(idx<0) return; // couldn't locate the node - bail, no edit
    var span=document.createElement('span');
    span.setAttribute('data-al-noedit','1');
    span.setAttribute('contenteditable','true');
    span.textContent=tn.nodeValue||'';
    span.style.outline='2px solid #6ee7b7';
    span.__alOldRaw=tn.nodeValue||'';
    span.__alOld=(tn.nodeValue||'').replace(/\\s+/g,' ').trim();
    span.__alPath=pathTo(container);
    span.__alTextIndex=idx;
    try{ tn.parentNode.replaceChild(span, tn); }catch(e){ return; }
    editing=span;
    try{ span.focus(); placeCaretEnd(span); }catch(e){}
    showBar(span);
  }
  // Hover affordance (only when armed and not mid-edit). Highlight leaves AND
  // inline-only containers (their bare text is per-node editable, ENG-6856).
  document.addEventListener('mouseover', function(e){
    if(!canEdit()) return; var el=e.target;
    if(editable(el)||inlineOnlyEditable(el)){
      // ENG-9383: remember what the author's cursor was before we overwrite it.
      // Blanking it on the way out would delete an inline cursor the artefact
      // set itself; '' is a legitimate saved value meaning "there was none".
      if(el.__alCur===undefined) el.__alCur = el.style.cursor || '';
      el.style.cursor='text'; el.style.outline=el.style.outline||'1px dashed rgba(110,231,183,.6)'; el.__alHover=1; hoverCued.push(el);
    }
  });
  document.addEventListener('mouseout', function(e){
    var el=e.target; if(el&&el.__alHover&&el!==editing){ el.style.outline=''; restoreCursor(el); el.__alHover=0; dropHoverCue(el); }
  });
  // ENG-9383 (CodeRabbit on #4956): the cue is an outline AND an I-beam cursor.
  // Clearing only the outline leaves the text still saying "you can type here"
  // on a page that will now refuse the click - the same misdirection this ticket
  // is about, one property over.
  function restoreCursor(el){
    if(el.__alCur!==undefined){ try{ el.style.cursor=el.__alCur; }catch(e){} el.__alCur=undefined; }
  }
  // Keep hoverCued bounded. Without this it grows once per hovered element for
  // the life of the page and pins every one of them against GC - a list that
  // only ever empties when the viewer toggles the mode off is a leak on any page
  // nobody toggles.
  function dropHoverCue(el){
    var i=hoverCued.indexOf(el); if(i!==-1) hoverCued.splice(i,1);
  }
  // ENG-9383: drop every outline the mouseover cue left behind, without waiting
  // for a mouseout that may never come - the mode can go off while the pointer is
  // parked on the text, and then the artefact wears a dashed "editable" outline
  // on a page where you no longer can. Tracked in a list rather than queried back
  // out of the DOM: more than one element can carry the cue if the pointer moved
  // between mouseover and mouseout, and the bridge should not depend on a
  // selector matching an inline style it wrote.
  function clearHoverCues(){
    for(var i=0;i<hoverCued.length;i++){
      var el=hoverCued[i];
      if(el && el.__alHover && el!==editing){ try{ el.style.outline=''; }catch(e){} restoreCursor(el); el.__alHover=0; }
    }
    hoverCued.length=0;
  }
  document.addEventListener('click', function(e){
    if(!canEdit()) return;
    var sel=document.getSelection(); if(sel && String(sel).trim()) return; // a selection => comment flow
    var el=e.target;
    if(editable(el)){ e.preventDefault(); e.stopPropagation(); startEdit(el); return; }
    // ENG-6856: a click on bare text inside an inline-only container edits that
    // specific run. The clicked text node's parent must be the editable element
    // (a click landing inside an inline child is handled by editable() above).
    var tn=textNodeAtPoint(e.clientX, e.clientY);
    if(tn && tn.parentElement===el && inlineOnlyEditable(el) && (tn.nodeValue||'').trim()){
      e.preventDefault(); e.stopPropagation(); startEditTextNode(tn, el);
    }
  }, true);
  // ENG-8477: reach content the artefact covers with its OWN overlay.
  //
  // A deck built from the canonical template lays two full-viewport click zones
  // over its slides (.clickzone, position:fixed, z-index:40) to page forward and
  // back. The template's own comment says it plainly: "the click zones sit above
  // slide content by design, so any interactive element inside a slide needs
  // position:relative + a higher z-index to stay clickable". Slide TEXT is not an
  // interactive element and never gets one.
  //
  // So every click a viewer aimed at a paragraph landed on #zone-prev/#zone-next.
  // The handler above read e.target, got an empty div, editable() rejected it on
  // the text check, and no editor opened - while the zone's own handler paged the
  // deck. A single-page artefact has no zones and the identical bridge works
  // perfectly on it, which is precisely why editing "works in web page formats
  // but not decks". Verified on real published content, not just the template.
  //
  // Why a MODE rather than always-on. One press cannot mean both "page the deck"
  // and "edit this text". The zones are the deck's primary navigation, so
  // silently stealing clicks from them would trade one broken gesture for
  // another. The shell gives an armed viewer an explicit Edit-text toggle; until
  // they turn it on, every line below is inert and the artefact behaves exactly
  // as it does today. Anonymous viewers never arm at all.
  //
  // Why window + capture. Same phase argument as ENG-8500: the artefact's script
  // is appended before the bridge, so registration order cannot be won, but the
  // capture path runs window -> document -> ... -> target and therefore beats a
  // handler bound to the zone itself no matter when it was registered.
  // stopImmediatePropagation then keeps the deck from also paging.
  //
  // Deliberately general: nothing here knows about .clickzone or any class name.
  // It fixes any artefact that covers its own content.
  window.addEventListener('click', function(e){
    if(!canEdit()) return;
    var sel=document.getSelection(); if(sel && String(sel).trim()) return; // selection => comment flow
    var t=e.target;
    // Our own toolbar (Save/Cancel) must keep its clicks.
    if(t && t.closest && t.closest('[data-al-noedit]')) return;
    // Already directly on editable content: the document-level handler above
    // covers it, and duplicating the work here would start the edit twice.
    if(editable(t)||inlineOnlyEditable(t)) return;
    var el=editableAtPoint(e.clientX, e.clientY);
    // Nothing editable under the pointer - leave the click completely alone so
    // click-to-page still works while the viewer is moving between slides.
    if(!el) return;
    // Suppress ONLY once an edit is actually starting. Suppressing up front left
    // the inline-only fallback below as a dead click: no editor opened AND the
    // deck could not page, because the event was already stopped.
    if(editable(el)){ e.preventDefault(); e.stopImmediatePropagation(); startEdit(el); return; }
    var tn=textNodeAtPointThrough(e.clientX, e.clientY, el);
    if(tn && tn.parentElement===el && (tn.nodeValue||'').trim()){
      e.preventDefault(); e.stopImmediatePropagation(); startEditTextNode(tn, el); return;
    }
    // An inline-only container whose exact text run we could not resolve (no
    // caret API, or the point fell between runs). Editing the whole container
    // would flatten its nested markup, so leave the click to the artefact and
    // let it page as it normally would.
  }, true);
  // ENG-8477: the same deck template pulls focus back to the document on every
  // pointer press, to keep its arrow keys working when the iframe loses focus:
  //
  //   function grabFocus(){ try{ window.focus(); document.body.focus(); }catch(e){} }
  //   addEventListener('pointerdown', grabFocus);
  //
  // A bare addEventListener at artefact top level is window + bubble phase, so a
  // window CAPTURE listener still gets there first. Without this guard, clicking
  // into an open editor to place the caret hands focus straight to <body> and the
  // viewer cannot type - a second, independent breaker that would have survived
  // the overlay fix on its own.
  //
  // Scoped as tightly as it can be: only while an edit is open, and only for a
  // press that lands inside the editor. A press anywhere else is the viewer
  // leaving, and the artefact keeps it.
  window.addEventListener('pointerdown', function(e){
    // ENG-10632: "inside the editor" is decided through the hit stack, not from
    // e.target, which a deck's click zone always wins. See pointInEditor.
    var where=pointInEditor(e);
    if(!where) return;
    e.stopImmediatePropagation();
    if(where==='through'){
      // The overlay is the browser's hit target, so its default action would
      // move focus off the editor and place no caret in the text. Take both over.
      try{ e.preventDefault(); }catch(err){}
      try{ editing.focus(); }catch(err){}
      // The press is suppressed either way, so a probe that cannot resolve a
      // point inside the editor must not leave it without a caret: keep one the
      // editor already holds, otherwise put it at the end of the text.
      if(!placeCaretAtPointThrough(e.clientX, e.clientY, editing)) keepCaretIn(editing);
    }
  }, true);
  // ENG-10632: the same press also arrives as mousedown and click, and the deck
  // can page on either. Only a press that went THROUGH an overlay into the open
  // editor is swallowed; a direct press keeps its native behaviour (caret,
  // drag-select), and a press anywhere else is the viewer leaving and is left to
  // the artefact.
  window.addEventListener('mousedown', function(e){
    if(pointInEditor(e)!=='through') return;
    try{ e.preventDefault(); }catch(err){}
    e.stopImmediatePropagation();
  }, true);
  window.addEventListener('click', function(e){
    if(pointInEditor(e)!=='through') return;
    try{ e.preventDefault(); }catch(err){}
    e.stopImmediatePropagation();
  }, true);
  // ENG-8477: the hover affordance has the same blind spot as the click did -
  // mouseover reports the overlay, so a viewer in edit mode would get no cue that
  // slide text can be edited at all. Probing the stack on every mousemove would
  // be wasteful, so this only runs in edit mode, only when the direct target is
  // not already editable (that case the mouseover handler above still covers),
  // and no more than once per ~80ms.
  var hoverProbeAt=0, hoverEl=null;
  function clearDeepHover(){
    if(hoverEl && hoverEl!==editing && hoverEl.__alHover){ try{ hoverEl.style.outline=''; }catch(e){} hoverEl.__alHover=0; }
    hoverEl=null;
  }
  document.addEventListener('mousemove', function(e){
    if(!canEdit()){ clearDeepHover(); return; }
    var t=e.target;
    if(editable(t)||inlineOnlyEditable(t)){ clearDeepHover(); return; }
    var now=Date.now(); if(now-hoverProbeAt<80) return; hoverProbeAt=now;
    var el=editableAtPoint(e.clientX, e.clientY);
    if(el===hoverEl) return;
    clearDeepHover();
    if(el){ try{ el.style.outline=el.style.outline||'1px dashed rgba(110,231,183,.6)'; }catch(e){} el.__alHover=1; hoverEl=el; }
  });
  // ENG-8500: while the inline editor is focused, the artefact's OWN keyboard
  // handlers must not see the keystroke.
  //
  // Decks ship as a single self-contained file with their own slide navigation
  // bound to space and the arrow keys, and that script is agent-authored content
  // we neither control nor can retro-patch - the published object is immutable,
  // so every deck already out there has its handler baked in. It also runs FIRST:
  // the bridge is appended after the artefact HTML (frame.srcdoc = html +
  // MARKUP_BRIDGE in publisher.ts), so the deck registers its listener before we
  // register ours.
  //
  // The consequence was not a keyboard annoyance, it was silent data corruption.
  // The deck's handler called preventDefault() on space, so the spaces a viewer
  // typed never reached the editor and words concatenated -
  // "albreakevenreached in June 2026" - with Save sitting right there. In English
  // prose a reader might catch it; in a number, a product name or a URL they
  // would not.
  //
  // Registration order cannot be won, so win on PHASE instead. The capture path
  // is window -> document -> ... -> target, so a capture listener on the window object
  // fires before any document-level or bubble-phase listener no matter when it
  // was registered. stopImmediatePropagation() then keeps the event from ever
  // reaching the deck.
  //
  // The one case this does not beat is an artefact that itself registers on
  // the window object with capture BEFORE the bridge loads. Deck templates use
  // document.addEventListener for keydown, so that is theoretical rather
  // than observed - but it is a real limit and worth knowing before someone
  // assumes this is airtight.
  //
  // Note what is deliberately NOT called for ordinary keys: preventDefault(). The
  // whole point is that space, arrows, Home/End and PageUp/PageDown do their
  // normal text-editing work. We suppress the artefact, not the browser.
  window.addEventListener('keydown', function(e){
    // Not editing: the artefact keeps its keyboard entirely. Slide navigation
    // must behave exactly as it does today (ENG-8500 AC2).
    if(!editing) return;
    // Editing, but the keystroke is going somewhere else (the viewer clicked
    // away, or is in the shell's own UI). Not ours to swallow.
    var t=e.target;
    var inEditor = t===editing || (editing.contains && t && t.nodeType && editing.contains(t));
    if(!inEditor) return;

    // Escape cancels the edit; Enter commits. Both are deliberate terminal
    // actions rather than text input, so they DO preventDefault - Enter must not
    // also insert a newline into the field it is closing. Shift+Enter is left
    // alone as a genuine newline.
    if(e.key==='Escape'){ e.preventDefault(); e.stopImmediatePropagation(); endEdit(false); return; }
    if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); e.stopImmediatePropagation(); endEdit(true); return; }

    // Everything else: let the browser handle it as text input, and stop the
    // artefact's own handlers from seeing it at all.
    e.stopImmediatePropagation();
  }, true);

  document.addEventListener('mouseup', function(){
    if(editing) return; // a selection inside the editor is for the cursor, not a comment
    var sel = document.getSelection();
    var text = sel ? String(sel).trim() : '';
    if(!text || sel.rangeCount === 0){ post({type:'clear'}); return; }
    var range = sel.getRangeAt(0);
    var r = range.getBoundingClientRect();
    post({type:'selection', text:text, target:anchorFor(range), rect:{top:r.top,left:r.left,bottom:r.bottom,right:r.right,width:r.width,height:r.height}});
  });
  document.addEventListener('scroll', function(){ if(!editing) post({type:'clear'}); }, true);

  // ENG-6847: right-click any element to comment on it. Only fires once the shell
  // (or console preview) arms commenting - so the browser's native menu is left
  // intact for anon public viewers. Skip when text is selected (that's the
  // selection-comment flow) and skip our own injected UI (data-al-noedit). On a
  // hit we suppress the native menu and post the element reference + anchor + rect.
  document.addEventListener('contextmenu', function(e){
    if(!commentEnabled || editing) return;
    var sel=document.getSelection(); if(sel && String(sel).trim()) return; // selection => comment via mouseup
    var el=e.target;
    if(!el || el.nodeType!==1 || el===document.body || el===document.documentElement) return;
    if(el.closest && el.closest('[data-al-noedit]')) return; // our toolbar/prompt, not content
    e.preventDefault();
    var r=el.getBoundingClientRect();
    post({type:'element', label:describeEl(el), target:anchorOf(el), rect:{top:r.top,left:r.left,bottom:r.bottom,right:r.right,width:r.width,height:r.height}});
  });

  // Control channel from the shell (arm editing/commenting; relay save result).
  // Only the shell (window.parent) may arm - a nested iframe the artifact embeds
  // must not be able to spoof enable-edit/enable-comment (server auth still gates
  // the mutation, but keep the client gate unspoofable too).
  window.addEventListener('message', function(e){
    if(e.source!==window.parent) return;
    var d=e.data; if(!d || d['${MARKUP_CONTROL_MARKER}']!==true) return;
    if(d.type==='enable-edit'){ editEnabled=true; }
    else if(d.type==='enable-comment'){ commentEnabled=true; }
    // ENG-8477: the viewer toggled Edit mode in the shell. Turning it OFF must
    // also close anything open and drop the hover cue, or the artefact is left
    // wearing our outlines with no way to clear them.
    // ENG-9383: clearDeepHover() only knows the deep-probe path's outline. The
    // ENG-6821 mouseover path marks its own element with __alHover and relies on
    // mouseout to undo it - which never fires if the pointer is still sitting on
    // the text when the mode goes off, leaving a dashed "you can edit this"
    // outline on a page where you now cannot. Clear both.
    else if(d.type==='set-edit-mode'){
      editMode = !!d.on;
      if(!editMode){ if(editing) endEdit(false); clearDeepHover(); clearHoverCues(); }
    }
    else if(d.type==='edit-result'){
      if(d.ok){ /* republish hot-swaps the iframe; nothing to do */ }
      else { /* leave the (now non-editable) text; the published version is the source of truth */ }
    }
  });
})();</script>`;

/** Append the selection bridge to artefact HTML before it's set as srcDoc. */
export function injectMarkupBridge(content: string): string {
  return content + MARKUP_BRIDGE_SCRIPT;
}

/**
 * Compose the chat message: the selected text quoted as context, then the
 * comment, then (when the selection sat inside an anchored element) a target
 * hint so the agent edits that element by its stable id rather than guessing
 * from a text-match (ENG-6802). `target` is a CSS selector like
 * `[data-al-id="hero"]` or `#price`; falsy values are omitted.
 */
export function composeMarkupMessage(selectedText: string, comment: string, target?: string): string {
  const quoted = selectedText
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
  const hint = target?.trim()
    ? `\n\n(Augmented Live: the selected text is inside \`${target.trim()}\` - edit that element, and keep its id / data-al-id when you re-publish.)`
    : '';
  return `${quoted}\n\n${comment.trim()}${hint}`;
}

/**
 * Compose the chat message for an *element* comment (ENG-6847 right-click). Unlike
 * composeMarkupMessage (which quotes a text selection), this quotes a single-line
 * human-readable element reference - `image (hero.png)`, `background image`,
 * `<section> Pricing` - then the comment, then the structural target hint so the
 * agent edits that exact element by its stable id rather than guessing. `target`
 * is a CSS selector like `[data-al-id="hero"]` or `#price`; falsy values are
 * omitted.
 */
export function composeElementMarkupMessage(reference: string, comment: string, target?: string): string {
  const ref = reference.trim();
  const quoted = ref ? `> ${ref}\n\n` : '';
  const hint = target?.trim()
    ? `\n\n(Augmented Live: this comment is about the element \`${target.trim()}\` - edit that element, and keep its id / data-al-id when you re-publish.)`
    : '';
  return `${quoted}${comment.trim()}${hint}`;
}
